import moment from "moment/moment";
import momentTimezone from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { Calendar } from "primereact/calendar";
import { Column, Row, Spacer } from "../../../../components/Layout/Layout";
import Button from "../../../../ui-lib/components/Button/Button";
import Table from "../../../../ui-lib/components/Tables/Table";
import TableCell from "../../../../ui-lib/components/Tables/TableCell";
import Icon from "../../../../ui-lib/components/Icon";
import Badge, { BadgeProps } from "../../../../ui-lib/components/Badges/Badge";
import useUser from "../../../../core/user/useUser";
import useTranslations from "../../../../core/i18n/useTranslations";
import { NewActivityModal } from "../NewActivityModal";
import { getPlannedActivities } from "../../../../core/api/objects/objects";
import { notifyApiErrors } from "../../../../core/helpers/helpers";
import LoadingSpinner from "../../../../ui-lib/components/Loading/LoadingSpinner";
import {
  ICalendarActivities,
  ICalendarActivity,
} from "../../../../core/api/objects/types";
import { getOrganizationIntegrations } from "../../../../core/api/organizations/organizations";
import { IOrgIntegration } from "../../../../core/api/organizations/types";
import useSharedObject from "../../SharedObjectContext/useSharedObject";
import {
  normalizeDate,
  userDateFormat,
} from "../../../../ui-lib/utils/userDateFormat";
import { DeleteActivityModal } from "../DeleteActivityModal";
import { getStoredToken } from "../../../../core/user/token";

import styles from "./PlanningOverview.module.css";

export const errorStatuses = [
  "ExecuteFailure",
  "ExecuteError",
  "Aborted",
  "FailedAbort",
];

export const completedStatuses = [
  "Complete",
  "Aborted",
  "FailedAbort",
  "ExecuteSuccess",
  "ExecuteFailure",
  "ExecuteError",
];

const timezoneId =
  getStoredToken()?.guiConfig.timezone.timezoneName ?? "Europe/Stockholm";

const columns = (t: (key: string) => string) => [
  {
    fieldTemplate(rowData: ICalendarActivity) {
      return rowData.timingType === "Recurring" ? (
        <TableCell value={<Icon name="refresh" size={16} />} />
      ) : null;
    },
    style: {
      minWidth: "unset",
      width: "48px",
      textAlign: "center",
    },
  },
  {
    fieldTemplate(rowData: ICalendarActivity) {
      const startTime = momentTimezone(rowData.executionTime)
        .tz(timezoneId)
        .format("HH:mm");
      return (
        <Column align="start">
          <span>{startTime}</span>
          <TableCell
            className={styles.integrationName}
            value={rowData?.integration}
          />
        </Column>
      );
    },
  },
  {
    fieldTemplate(rowData: ICalendarActivity) {
      let statusVariant = "success";
      if (errorStatuses.includes(rowData.status)) statusVariant = "error";
      if (
        !errorStatuses.includes(rowData.status) &&
        rowData.status !== "Complete" &&
        rowData.status !== "ExecuteSuccess"
      )
        statusVariant = "info";
      return (
        <Badge
          withHighlightedText
          variant={statusVariant as BadgeProps["variant"]}
          title={t(`Objects:${rowData.status}`)}
        />
      );
    },
    style: { textAlign: "right" },
  },
];

const getDayActivities = (
  data: ICalendarActivities | undefined,
  date: Date | null | undefined,
): ICalendarActivity[] =>
  data?.[normalizeDate(moment(date ?? undefined).format("YYYY-MM-DD"))] ?? [];

export const PlanningOverview = ({ objectId }: { objectId: number }) => {
  const t = useTranslations();
  const { config, authenticatedRequest } = useUser();
  const { data: objectData } = useSharedObject();

  const createActivityPermission = config?.show.includes(
    "PlanningEngineActivityWrite",
  );

  const [loading, setLoading] = useState(true);
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const [newActivityModalOpen, setNewActivityModalOpen] = useState(false);
  const [deleteActivityId, setDeleteActivityId] = useState<string>();
  const [editActivityId, setEditActivityId] = useState<string>();
  const [canEditActivity, setCanEditActivity] = useState(true);
  const [currentViewStartMonth, setCurrentViewStartMonth] = useState<string>(
    moment().startOf("month").toISOString(),
  );
  const [activitiesData, setActivitiesData] = useState<ICalendarActivities>();
  const [selectedDate, setSelectedDate] = useState<Date | null>();
  const [selectedDayActivities, setSelectedDayActivities] = useState<
    ICalendarActivity[]
  >([]);
  const [integrationOptions, setIntegrationOptions] =
    useState<IOrgIntegration[]>();

  // guards against out-of-order fetch responses overwriting current state when paging quickly
  const fetchIdRef = useRef(0);
  // marks a fetch as triggered by month paging (which must not touch the selected day's list)
  const isPagingRef = useRef(false);

  const fetchOrgIntegrations = async () => {
    if (objectData?.organizationId) {
      setLoading(true);
      try {
        const integrationsData = await getOrganizationIntegrations(
          objectData.organizationId,
          authenticatedRequest,
        );
        setIntegrationOptions(integrationsData.data);
      } catch (error: any) {
        notifyApiErrors(error.response?.data?.errors);
      } finally {
        setLoading(false);
      }
    }
  };

  // refreshSelectedDay controls whether the "Planned activities" list is refreshed:
  // true for initial load, and after create/delete; false when paging the calendar
  const fetchActivities = async (refreshSelectedDay = false) => {
    const fetchId = ++fetchIdRef.current;
    setIsCalendarLoading(true);
    try {
      // the calendar shows two months starting at currentViewStartMonth, plus a few
      // leading/trailing days from the adjacent months
      const viewStart = moment(currentViewStartMonth)
        .startOf("month")
        .subtract(1, "week");
      const viewEnd = moment(currentViewStartMonth)
        .add(1, "month")
        .endOf("month")
        .add(1, "week");
      const result = await getPlannedActivities(
        objectId,
        viewStart.toISOString(),
        viewEnd.toISOString(),
        authenticatedRequest,
      );
      if (fetchId !== fetchIdRef.current) return;
      setActivitiesData(result.data);
      if (refreshSelectedDay) {
        let dayData = result.data;
        const selected = moment(selectedDate ?? undefined);
        // the selected day may sit outside the visible two-month window (e.g. paged
        // away, then created/deleted an activity); fetch its month too so its list
        // stays accurate without widening the main bounded request
        if (!selected.isBetween(viewStart, viewEnd, undefined, "[]")) {
          const selResult = await getPlannedActivities(
            objectId,
            selected.clone().startOf("month").toISOString(),
            selected.clone().endOf("month").toISOString(),
            authenticatedRequest,
          );
          if (fetchId !== fetchIdRef.current) return;
          dayData = { ...result.data, ...selResult.data };
        }
        setSelectedDayActivities(getDayActivities(dayData, selectedDate));
      }
    } catch (error: any) {
      if (fetchId !== fetchIdRef.current) return;
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
        setIsCalendarLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchOrgIntegrations();
  }, []);

  useEffect(() => {
    // refresh the selected day's list on initial load, but paging leaves the list untouched
    const paging = isPagingRef.current;
    isPagingRef.current = false;
    fetchActivities(!paging);
  }, [currentViewStartMonth]);

  useEffect(() => {
    const nextMonthButton = document.querySelector(
      '[data-pc-section="nextbutton"]:not([style*="visibility: hidden"])',
    );
    const prevMonthButton = document.querySelector(
      '[data-pc-section="previousbutton"]:not([style*="visibility: hidden"])',
    );
    if (isCalendarLoading) {
      nextMonthButton?.setAttribute("disabled", "true");
      prevMonthButton?.setAttribute("disabled", "true");
    } else {
      nextMonthButton?.removeAttribute("disabled");
      prevMonthButton?.removeAttribute("disabled");
    }
  }, [isCalendarLoading]);

  return (
    <>
      <div className="w-100">
        <Row type="space" className="w-100">
          <Column align="start">
            <h1>{t("Objects:planning_overview_title")}</h1>
            <Spacer size={8} />
            <p>{t("Objects:planning_overview_description")}</p>
          </Column>
          <Column align="end">
            {createActivityPermission && (
              <Button
                text={t("Objects:plan_new_activity")}
                image="plus-circle"
                variant="border-link"
                onClick={() => setNewActivityModalOpen(true)}
              />
            )}
          </Column>
        </Row>
        <Spacer size={32} />
        {loading ? (
          <LoadingSpinner theme="primary" />
        ) : (
          <div className={classnames(styles.planningWrapper)}>
            <div
              className={classnames(styles.planningCalendar)}
              style={{
                borderStyle: "solid",
                borderWidth: "0 1px 0 0",
                borderColor: "var(--Grey-200)",
              }}
            >
              <Calendar
                inline
                disabled={isCalendarLoading}
                numberOfMonths={2}
                value={selectedDate}
                viewDate={new Date(currentViewStartMonth)}
                onViewDateChange={(e) => {
                  isPagingRef.current = true;
                  setCurrentViewStartMonth(
                    moment(e.value).startOf("month").toISOString(),
                  );
                }}
                onChange={(val) => {
                  setSelectedDate(val.target.value);
                  setSelectedDayActivities(
                    getDayActivities(activitiesData, val.target.value),
                  );
                }}
                dateTemplate={(el) => {
                  const dayString = `${el.year}-${el.month + 1}-${el.day}`;
                  const activitiesDays = Object.keys(activitiesData ?? {});
                  return (
                    <div
                      className={classnames({
                        [styles.withActions]: activitiesDays.some(
                          (date: string) => normalizeDate(dayString) === date,
                        ),
                      })}
                    >
                      {el.day}
                    </div>
                  );
                }}
                panelStyle={{
                  borderWidth: "0",
                }}
              />
            </div>
            <div className={classnames(styles.planningList)}>
              <h2>{t("Objects:planned_activities")}</h2>
              <p>{`${t("Common:for")} ${moment(selectedDate).format(
                userDateFormat(config?.dateFormat ?? "YYYY-MM-DD"),
              )}`}</p>
              <Spacer size={16} />
              <Table<ICalendarActivity>
                hideHeader
                hideEmptyMessage
                rowActionContextMenu
                items={selectedDayActivities}
                rowActions={[
                  {
                    text: t("Common:view"),
                    icon: "eye",
                    iconVariant: "secondary",
                    iconSize: 16,
                    onClick: (rowData) => {
                      setEditActivityId(rowData.activityId);
                      setNewActivityModalOpen(true);
                      setCanEditActivity(false);
                    },
                    hideForRow: (rowData) =>
                      !completedStatuses.includes(rowData.status),
                  },
                  {
                    text: t("Common:label_edit"),
                    icon: "pencil",
                    iconVariant: "secondary",
                    iconSize: 16,
                    onClick: (rowData) => {
                      setEditActivityId(rowData.activityId);
                      setNewActivityModalOpen(true);
                    },
                    hideForRow: (rowData) =>
                      completedStatuses.includes(rowData.status),
                  },
                  ...(createActivityPermission
                    ? [
                        {
                          text: t("Common:delete"),
                          icon: "trash",
                          iconVariant: "secondary" as const,
                          iconSize: 16,
                          onClick: (rowData: ICalendarActivity) => {
                            setDeleteActivityId(rowData.activityId);
                          },
                          hideForRow: (rowData: ICalendarActivity) =>
                            rowData.status === "Aborted",
                        },
                      ]
                    : []),
                ]}
                columns={columns(t)}
              />
            </div>
          </div>
        )}
      </div>
      {createActivityPermission && newActivityModalOpen && objectData?.id && (
        <NewActivityModal
          userId={objectData.id}
          activityId={editActivityId}
          canEdit={canEditActivity}
          isOpen={newActivityModalOpen}
          integrations={integrationOptions}
          onClose={(refetch = false) => {
            setNewActivityModalOpen(false);
            setEditActivityId(undefined);
            setCanEditActivity(true);
            if (refetch) fetchActivities(true);
          }}
        />
      )}
      {createActivityPermission && objectData?.id && (
        <DeleteActivityModal
          activityId={deleteActivityId}
          userId={objectData.id}
          onClose={() => {
            setDeleteActivityId(undefined);
            fetchActivities(true);
          }}
        />
      )}
    </>
  );
};
